import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useState, useEffect } from "react";
import {
  PLATFORM_FEATURE_FLAGS,
  DEFAULT_REMOTE_CONFIG_DEFAULTS,
  type IFeatureFlagDefinition,
} from "@/constants/feature-flags";
import {
  type IGetRemoteConfigTemplateResponse,
  type IUpdateRemoteConfigParameterResponse,
  type IRemoteConfigParameterItem,
} from "common247";
import { callFunction } from "@/services/firebase/helpers";
import { type FirebaseServices } from "@/services/firebase/config";

const ASYNC_STORAGE_OVERRIDE_KEY = "@memories_remote_config_overrides";

export interface IFeatureFlagState {
  key: string;
  name: string;
  value: boolean;
  defaultValue: boolean;
  description: string;
  category: string;
  source: "remote" | "default" | "override";
}

/**
 * Service managing Firebase Remote Config client integration, baseline defaults,
 * local dev overrides, and real-time subscription notifications.
 */
export class RemoteConfigService {
  private static instance: RemoteConfigService | null = null;
  private rcInstance: any = null;
  private overrides: Record<string, boolean> = {};
  private remoteValues: Record<string, any> = {};
  private listeners: Set<() => void> = new Set();
  private initialized: boolean = false;
  private currentUserBetaGroup: string | null = null;

  private constructor() {}

  /**
   * Singleton accessor.
   */
  public static getInstance(): RemoteConfigService {
    if (!RemoteConfigService.instance) {
      RemoteConfigService.instance = new RemoteConfigService();
    }
    return RemoteConfigService.instance;
  }

  /**
   * Sets or clears the active user's Beta Group assignment for condition evaluation.
   */
  public setUserBetaGroup(betaGroup?: string | null): void {
    const clean = typeof betaGroup === "string" && betaGroup.trim() ? betaGroup.trim().toLowerCase() : null;
    if (this.currentUserBetaGroup !== clean) {
      this.currentUserBetaGroup = clean;
      this.notify();
    }
  }

  /**
   * Gets the active user's Beta Group name if assigned.
   */
  public getUserBetaGroup(): string | null {
    return this.currentUserBetaGroup;
  }

  /**
   * Returns true if the user is in a beta group, or matches a specific group.
   */
  public isUserInBetaGroup(targetGroup?: string): boolean {
    if (!this.currentUserBetaGroup) return false;
    if (!targetGroup) return true;
    return this.currentUserBetaGroup === targetGroup.trim().toLowerCase();
  }

  /**
   * Initializes Firebase Remote Config and loads persisted local overrides.
   */
  public async initialize(app?: any): Promise<void> {
    if (this.initialized) return;

    // 1. Load persisted local overrides
    try {
      const stored = await AsyncStorage.getItem(ASYNC_STORAGE_OVERRIDE_KEY);
      if (stored) {
        this.overrides = JSON.parse(stored);
      }
    } catch (storageErr) {
      console.warn("[RemoteConfigService] Failed to load local overrides:", storageErr);
    }

    // 2. Initialize Firebase Remote Config on supported platforms
    if (Platform.OS === "web" && typeof window !== "undefined") {
      const apiKey = app?.options?.apiKey || process.env.EXPO_PUBLIC_API_KEY;
      const projectId = app?.options?.projectId || process.env.EXPO_PUBLIC_PROJECT_ID;
      const isPlaceholderKey =
        !apiKey ||
        apiKey === "xxx" ||
        apiKey === "dummy" ||
        apiKey.toLowerCase().includes("placeholder");
      const isEmulator =
        process.env.EXPO_PUBLIC_USE_EMULATOR === "1" ||
        (typeof projectId === "string" && projectId.startsWith("demo-"));

      if (isPlaceholderKey || isEmulator) {
        console.info(
          "[RemoteConfigService] Running in local/emulator mode; skipping remote fetch and using defaults."
        );
        this.initialized = true;
        this.notify();
        return;
      }

      try {
        let rcModule: any = null;
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          rcModule = require("firebase/remote-config");
        } catch {
          try {
            rcModule = await import("firebase/remote-config");
          } catch {
            rcModule = null;
          }
        }
        if (rcModule && app) {
          const { getRemoteConfig, fetchAndActivate, getAll } = rcModule;
          this.rcInstance = getRemoteConfig(app);
          this.rcInstance.settings = {
            fetchTimeoutMillis: 10000,
            minimumFetchIntervalMillis: 3600000, // 1 hour
          };
          this.rcInstance.defaultConfig = DEFAULT_REMOTE_CONFIG_DEFAULTS;

          // Attempt initial fetch & activate
          fetchAndActivate(this.rcInstance)
            .then(() => {
              const all = getAll(this.rcInstance);
              for (const [k, v] of Object.entries(all)) {
                this.remoteValues[k] = v.asBoolean();
              }
              this.notify();
            })
            .catch((err) => {
              console.info(
                "[RemoteConfigService] Remote fetch deferred/offline, using defaults:",
                err?.message
              );
            });
        }
      } catch (importErr) {
        console.warn("[RemoteConfigService] Remote Config module unavailable:", importErr);
      }
    }

    this.initialized = true;
    this.notify();
  }

  /**
   * Fetches latest parameters from Firebase Remote Config and activates them.
   */
  public async fetchAndActivate(): Promise<boolean> {
    if (!this.rcInstance) {
      this.notify();
      return false;
    }

    try {
      const { fetchAndActivate, getAll } = await import("firebase/remote-config");
      const activated = await fetchAndActivate(this.rcInstance);
      const all = getAll(this.rcInstance);
      for (const [k, v] of Object.entries(all)) {
        this.remoteValues[k] = v.asBoolean();
      }
      this.notify();
      return activated;
    } catch (err) {
      console.warn("[RemoteConfigService] fetchAndActivate error:", err);
      return false;
    }
  }

  /**
   * Evaluates a boolean feature flag with priority: Local Override > Remote Config > Registered Default.
   */
  public getBoolean(key: string, defaultValue?: boolean): boolean {
    // 1. Check local override
    if (typeof this.overrides[key] !== "undefined") {
      return this.overrides[key];
    }

    // 2. Check live Remote Config value
    if (typeof this.remoteValues[key] !== "undefined") {
      return this.remoteValues[key];
    }

    // 3. Evaluate conditional Beta Group logic for early-access features
    if (key === "enable_beta_features" && this.currentUserBetaGroup) {
      return true;
    }

    // 4. Fall back to registered definition default or caller default
    const def = PLATFORM_FEATURE_FLAGS[key];
    if (def) {
      return def.defaultValue;
    }

    return defaultValue ?? true;
  }

  /**
   * Returns a complete catalog of all registered feature flags and their current evaluated states.
   */
  public getAllFlags(): Record<string, IFeatureFlagState> {
    const result: Record<string, IFeatureFlagState> = {};

    for (const [key, def] of Object.entries(PLATFORM_FEATURE_FLAGS)) {
      let value = def.defaultValue;
      let source: "remote" | "default" | "override" = "default";

      if (key === "enable_beta_features" && this.currentUserBetaGroup) {
        value = true;
        source = "remote";
      }

      if (typeof this.remoteValues[key] !== "undefined") {
        value = this.remoteValues[key];
        source = "remote";
      }

      if (typeof this.overrides[key] !== "undefined") {
        value = this.overrides[key];
        source = "override";
      }

      result[key] = {
        key,
        name: def.name,
        value,
        defaultValue: def.defaultValue,
        description: def.description,
        category: def.category,
        source,
      };
    }

    return result;
  }

  /**
   * Sets a local developer override for a feature flag.
   */
  public async setLocalOverride(key: string, value: boolean): Promise<void> {
    this.overrides[key] = value;
    try {
      await AsyncStorage.setItem(
        ASYNC_STORAGE_OVERRIDE_KEY,
        JSON.stringify(this.overrides)
      );
    } catch (err) {
      console.warn("[RemoteConfigService] Failed to save local override:", err);
    }
    this.notify();
  }

  /**
   * Clears a single local override, reverting to Remote Config or default value.
   */
  public async clearLocalOverride(key: string): Promise<void> {
    delete this.overrides[key];
    try {
      await AsyncStorage.setItem(
        ASYNC_STORAGE_OVERRIDE_KEY,
        JSON.stringify(this.overrides)
      );
    } catch (err) {
      console.warn("[RemoteConfigService] Failed to clear local override:", err);
    }
    this.notify();
  }

  /**
   * Clears all local developer overrides.
   */
  public async clearAllOverrides(): Promise<void> {
    this.overrides = {};
    try {
      await AsyncStorage.removeItem(ASYNC_STORAGE_OVERRIDE_KEY);
    } catch (err) {
      console.warn("[RemoteConfigService] Failed to clear all overrides:", err);
    }
    this.notify();
  }

  /**
   * Subscribes to feature flag changes.
   */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.error("[RemoteConfigService] Listener error:", err);
      }
    });
  }

  /**
   * Calls the backend Cloud Function to retrieve the current Remote Config template.
   */
  public static async fetchRemoteConfigTemplate(
    fb: FirebaseServices
  ): Promise<IGetRemoteConfigTemplateResponse> {
    return callFunction<{}, IGetRemoteConfigTemplateResponse>(
      fb,
      "getRemoteConfigTemplateFunc",
      {}
    );
  }

  /**
   * Calls the backend Cloud Function to update a Remote Config parameter.
   */
  public static async updateRemoteConfigParameter(
    fb: FirebaseServices,
    key: string,
    value: boolean | string | number,
    description?: string
  ): Promise<IUpdateRemoteConfigParameterResponse> {
    return callFunction<
      { key: string; value: boolean | string | number; description?: string },
      IUpdateRemoteConfigParameterResponse
    >(fb, "updateRemoteConfigParameterFunc", {
      key,
      value,
      description,
    });
  }
}

/**
 * React Hook for reactive feature flag gating in UI components.
 * Automatically updates component when flag value changes.
 *
 * @param {string} key - The feature flag key to inspect.
 * @param {boolean} [defaultValue] - Optional fallback default value.
 * @returns {boolean} Whether the feature flag is currently enabled.
 */
export function useFeatureFlag(key: string, defaultValue?: boolean): boolean {
  const service = RemoteConfigService.getInstance();
  const [enabled, setEnabled] = useState<boolean>(() =>
    service.getBoolean(key, defaultValue)
  );

  useEffect(() => {
    setEnabled(service.getBoolean(key, defaultValue));
    const unsubscribe = service.subscribe(() => {
      setEnabled(service.getBoolean(key, defaultValue));
    });
    return unsubscribe;
  }, [key, defaultValue]);

  return enabled;
}
